import type { SettingsPage, SettingItem } from "../types";
import { PROVIDER_SPECS } from "../../ai/providers";
import { useTransferDialogStore } from "../transferDialogStore";

/**
 * Settings > AI — the credentials and options behind the editor's `/ai` slash
 * commands (see src/ai/, src/editor/slashCommandPlugin.ts).
 *
 * One provider is active at a time. The `provider` select drives everything;
 * each provider's own fields are revealed with `visibleWhen`, the same shape
 * backend.ts uses for its per-cloud-service fields, so the page never shows
 * more than one provider's settings at once. Endpoint, headers and response
 * parsing come from that provider's ProviderSpec — only the key (and,
 * optionally, the model) are asked for here.
 */

/** The password + model pair every built-in provider needs, gated on the select. */
function providerFields(key: string, label: string, defaultModel: string): SettingItem[] {
  return [
    {
      key: `${key}ApiKey`,
      label: `${label}: API key`,
      type: "password",
      default: "",
      encrypted: true,
      description:
        "Encrypted with your project key and stored on this device only — open a project to view or change it. Separate from the keys the yaqatw plugin uses for its coding agents; setting one does not set the other.",
      visibleWhen: { key: "provider", equals: key },
    },
    {
      key: `${key}Model`,
      label: `${label}: Model`,
      type: "text",
      default: defaultModel,
      description: `Leave as-is unless you want a specific model. Default: ${defaultModel}.`,
      visibleWhen: { key: "provider", equals: key },
    },
  ];
}

const builtInProviderFields = PROVIDER_SPECS.flatMap((spec) =>
  providerFields(spec.key, spec.label, spec.defaultModel)
);

const aiSettings: SettingsPage = {
  id: "ai",
  label: "AI",
  order: 3,
  items: [
    {
      key: "providerHeading",
      label: "Provider",
      type: "heading",
      default: "",
      description:
        "Used by the editor's /ai slash commands. Requests go from this browser straight to the provider you choose.",
    },
    {
      key: "provider",
      label: "AI provider",
      type: "select",
      default: "",
      options: [
        { value: "", label: "Disabled" },
        ...PROVIDER_SPECS.map((spec) => ({ value: spec.key, label: spec.label })),
        { value: "custom", label: "Custom (OpenAI-compatible or your own)" },
      ],
      description: "Choose Disabled to hide the AI commands from the / menu.",
    },
    ...builtInProviderFields,

    {
      key: "customUrl",
      label: "Custom: Endpoint URL",
      type: "text",
      default: "",
      description:
        "Full URL to POST to, e.g. http://localhost:11434/v1/chat/completions. {model} and {key} are substituted if present.",
      visibleWhen: { key: "provider", equals: "custom" },
    },
    {
      key: "customApiKey",
      label: "Custom: API key",
      type: "password",
      default: "",
      encrypted: true,
      description:
        "Encrypted with your project key. Leave blank for a local endpoint that needs no credential.",
      visibleWhen: { key: "provider", equals: "custom" },
    },
    {
      key: "customModel",
      label: "Custom: Model",
      type: "text",
      default: "",
      visibleWhen: { key: "provider", equals: "custom" },
    },
    {
      key: "customBodyStyle",
      label: "Custom: Request format",
      type: "select",
      default: "openai",
      options: [
        { value: "openai", label: "OpenAI-compatible (chat/completions)" },
        { value: "anthropic", label: "Anthropic (messages)" },
        { value: "gemini", label: "Gemini (generateContent)" },
      ],
      description: "Which request and response shape your endpoint speaks.",
      visibleWhen: { key: "provider", equals: "custom" },
    },
    {
      key: "customAuthHeader",
      label: "Custom: Auth header name",
      type: "text",
      default: "Authorization",
      description: "Leave blank to send no auth header (e.g. a key already in the URL).",
      visibleWhen: { key: "provider", equals: "custom" },
    },
    {
      key: "customAuthPrefix",
      label: "Custom: Auth header prefix",
      type: "text",
      default: "Bearer ",
      description: "Prepended to the key, e.g. \"Bearer \". Include the trailing space.",
      visibleWhen: { key: "provider", equals: "custom" },
    },
    {
      key: "customResponsePath",
      label: "Custom: Response path",
      type: "text",
      default: "",
      description:
        "Dotted path to the reply text, e.g. choices.0.message.content. Leave blank to use the request format's default.",
      visibleWhen: { key: "provider", equals: "custom" },
    },

    {
      key: "maxTokens",
      label: "Maximum response length (tokens)",
      type: "number",
      default: 8192,
      min: 512,
      max: 64000,
      step: 512,
      description:
        "Raise it if long tables or checklists come back cut off. On models that reason before answering, this budget covers the reasoning too.",
    },

    {
      key: "transferHeading",
      label: "Move to another device",
      type: "heading",
      default: "",
      description:
        "Copy these settings to a phone or second computer with a QR code, instead of retyping the key. The code is encrypted with your project key, so the other device must have the same project open.",
    },
    {
      key: "showTransferCode",
      label: "Show transfer code",
      type: "action",
      default: "",
      description: "Displays a QR code holding your AI settings, to scan from your other device.",
      onClick: () => useTransferDialogStore.getState().open("show"),
    },
    {
      key: "scanTransferCode",
      label: "Scan transfer code",
      type: "action",
      default: "",
      description: "Opens the camera to read a code shown on your other device.",
      onClick: () => useTransferDialogStore.getState().open("scan"),
    },

    {
      key: "slashHeading",
      label: "Slash commands",
      type: "heading",
      default: "",
      description: "Type / in the editor to open the command menu.",
    },
    {
      key: "enableSlashAi",
      label: "Show AI commands in the / menu",
      type: "toggle",
      default: true,
      description: "Turn off to keep the / menu to plain insert commands only.",
    },
    {
      key: "customCommands",
      label: "Custom AI commands",
      type: "textarea",
      default: "",
      description:
        'A JSON array of your own commands, e.g. [{"name":"aiswot","title":"SWOT analysis","prompt":"Produce a SWOT analysis of the topic.","blocks":["heading","table"]}]. "name" is what you type after the slash; "blocks" is optional and limits what the AI may produce — any of heading, paragraph, bulletList, orderedList, checklist, table, blockquote.',
      visibleWhen: { key: "enableSlashAi", equals: true },
    },
  ],
};

export default aiSettings;
